import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Building2 } from 'lucide-react';

const BusinessSetup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    state: '',
    gstin: '',
    pan: '',
    phone: '',
    email: '',
    financialYear: '2024-25',
    invoicePrefix: 'INV',
    upiId: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.address || !formData.state || !formData.phone || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);

    try {
      console.log('Submitting business data:', formData);
      const response = await api.post('/api/business', formData);
      console.log('Business created response:', response.data);
      
      if (response.data && response.data.business && response.data.business.id) {
        localStorage.setItem('currentBusiness', response.data.business.id);
        toast.success('Business created successfully!');
        
        // Small delay to ensure localStorage is saved
        setTimeout(() => {
          navigate('/dashboard');
        }, 500);
      } else {
        console.error('Invalid response structure:', response.data);
        toast.error('Business created but response format unexpected');
      }
    } catch (error) {
      console.error('Business creation error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      
      const errorMessage = error.response?.data?.error 
        || error.response?.data?.detail 
        || error.message 
        || 'Failed to create business. Please try again.';
        
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-orange-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-2xl shadow-xl" data-testid="business-setup-card">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-4">
            <Building2 className="w-8 h-8 text-teal-700" />
          </div>
          <CardTitle className="text-3xl font-heading">Set Up Your Business</CardTitle>
          <CardDescription>Enter your business details to get started</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="business-setup-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="name">Business Name *</Label>
                <Input
                  id="name"
                  name="name"
                  placeholder="My Business Pvt Ltd"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="business-name-input"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="address">Address *</Label>
                <Textarea
                  id="address"
                  name="address"
                  placeholder="123 Business Street, City - 400001"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  rows={3}
                  data-testid="business-address-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="state">State *</Label>
                <Input
                  id="state"
                  name="state"
                  placeholder="Maharashtra"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  data-testid="business-state-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="gstin">GSTIN (Optional)</Label>
                <Input
                  id="gstin"
                  name="gstin"
                  placeholder="29XXXXX1234X1Z5"
                  value={formData.gstin}
                  onChange={handleChange}
                  data-testid="business-gstin-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pan">PAN (Optional)</Label>
                <Input
                  id="pan"
                  name="pan"
                  placeholder="ABCDE1234F"
                  value={formData.pan}
                  onChange={handleChange}
                  data-testid="business-pan-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  name="phone"
                  placeholder="+91 98765 43210"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  data-testid="business-phone-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="business@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="business-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="upiId">UPI ID (Optional)</Label>
                <Input
                  id="upiId"
                  name="upiId"
                  placeholder="business@paytm"
                  value={formData.upiId}
                  onChange={handleChange}
                  data-testid="business-upi-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="financialYear">Financial Year *</Label>
                <Input
                  id="financialYear"
                  name="financialYear"
                  placeholder="2024-25"
                  value={formData.financialYear}
                  onChange={handleChange}
                  required
                  data-testid="business-fy-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="invoicePrefix">Invoice Prefix *</Label>
                <Input
                  id="invoicePrefix"
                  name="invoicePrefix"
                  placeholder="INV"
                  value={formData.invoicePrefix}
                  onChange={handleChange}
                  required
                  data-testid="business-prefix-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-teal-700 hover:bg-teal-800"
              disabled={loading}
              data-testid="business-setup-submit-button"
            >
              {loading ? 'Creating Business...' : 'Create Business'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default BusinessSetup;